
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import LoginForm from "@/components/auth/LoginForm";
import { useToast } from "@/hooks/use-toast";
import { useUnifiedAuth } from "@/contexts/UnifiedAuthContext";
import { isTelegramWebApp, hasTelegramInitData } from "@/integrations/telegram/telegramService";
import { useTranslation } from "react-i18next";

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = useUnifiedAuth();
  const { t } = useTranslation();

  // Редирект если пользователь уже авторизован (после Magic Link)
  useEffect(() => {
    if (auth.isInitialized && auth.status === 'authenticated') {
      const returnTo = localStorage.getItem('returnTo');
      if (returnTo) {
        localStorage.removeItem('returnTo');
        navigate(returnTo);
      } else {
        navigate('/');
      }
    }
  }, [auth.status, auth.isInitialized, navigate]);

  // Проверяем, пришел ли пользователь после подтверждения email
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const confirmed = searchParams.get('confirmed');

    if (confirmed === 'true') {
      toast({
        title: t('auth.login.email_confirmed_title'),
        description: t('auth.login.email_confirmed_desc'),
        variant: "default",
      });

      // Удаляем параметр из URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location, toast, t]);

  const showDebug = false;

  return (
    <MainLayout>
      <div className="container-page flex items-center justify-center py-8 md:py-16">
        <div className="w-full max-w-md bg-card p-6 md:p-8 rounded-lg border shadow-sm">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold">{t('auth.login.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('auth.login.subtitle')}
            </p>
          </div>

          <LoginForm />

          {showDebug && (
            <div className="mt-8 p-4 bg-slate-100 text-xs font-mono rounded overflow-auto max-h-96 border border-slate-300 text-slate-800 break-all">
              <h3 className="font-bold mb-2 text-base">Debug Info</h3>
              <div className="space-y-1">
                <div><strong>User Agent:</strong> {navigator.userAgent}</div>
                <div><strong>isTelegramWebApp():</strong> {String(isTelegramWebApp())}</div>
                <div><strong>hasTelegramInitData():</strong> {String(hasTelegramInitData())}</div>
                <div><strong>initData length:</strong> {window.Telegram?.WebApp?.initData?.length || 0}</div>
                <div className="my-2 border-b border-slate-300"></div>
                <div className="font-bold">Auth Context State:</div>
                <div><strong>status:</strong> {auth.status}</div>
                <div><strong>loading:</strong> {String(auth.loading)}</div>
                <div><strong>isInitialized:</strong> {String(auth.isInitialized)}</div>
                <div><strong>isTelegram:</strong> {String(auth.isTelegram)}</div>
                <div><strong>isAuthenticating:</strong> {String(auth.isAuthenticating)}</div>
                <div><strong>error:</strong> {String(auth.error)}</div>
                <div className="my-2 border-b border-slate-300"></div>
                <div className="font-bold">Execution Log:</div>
                {auth.debugLog?.map((log: string, i: number) => (
                  <div key={i} className="text-[10px] leading-tight">{log}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Login;
